import { ILeaseAgreement, ITenantLeaseInvite, LeaseAgreementSource } from 'realhaus-sdk';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type TenantInviteFields = Partial<Pick<ITenantLeaseInvite, 'firstname' | 'lastname' | 'email'>>;

/**
 * An off-platform (NON_REALHAUS) lease whose tenant has not joined yet; the landlord supplies the
 * tenant's details through a lease invite instead.
 */
export const isInvitePending = (lease?: Pick<ILeaseAgreement, 'source' | 'tenantIds'>) =>
  lease?.source === LeaseAgreementSource.NON_REALHAUS &&
  (!lease.tenantIds || lease.tenantIds.length === 0);

/**
 * The invite payload to persist: trimmed names, trimmed and lowercased email. Anything else on the
 * loaded invite (e.g. its `id` or `invitationSent`) is intentionally left out.
 */
export const normalizeTenantInvite = (invite?: TenantInviteFields) => ({
  firstname: (invite?.firstname ?? '').trim(),
  lastname: (invite?.lastname ?? '').trim(),
  email: (invite?.email ?? '').trim().toLowerCase(),
});

export const isSameTenantInvite = (a?: TenantInviteFields, b?: TenantInviteFields) => {
  const left = normalizeTenantInvite(a);
  const right = normalizeTenantInvite(b);
  return (
    left.firstname === right.firstname &&
    left.lastname === right.lastname &&
    left.email === right.email
  );
};

/** @returns the message to show the landlord, or undefined when the invite is valid */
export const getTenantInviteError = (invite?: TenantInviteFields) => {
  const { firstname, lastname, email } = normalizeTenantInvite(invite);

  if (!firstname || !lastname || !email) {
    return 'Tenant first name, last name, and email address are required';
  }
  if (!EMAIL_PATTERN.test(email)) {
    return 'Enter a valid tenant email address';
  }
  return undefined;
};
